/**
 * useTeamMessages
 *
 * Fetches message history with a specific user from the REST API (Redis).
 * Listens to TEAM_MESSAGE socket events to append new messages in real-time.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useSocket } from '@/contexts/SocketContext';
import { SOCKET_EVENTS } from '@troia-v3/types';
import type { TeamMessageEvent, TeamChatMessage } from '@troia-v3/types';
import { teamChatService } from '../services/teamChatService';

export const useTeamMessages = (targetUserId: string, roomId: string | null) => {
  const queryClient = useQueryClient();
  const { socket } = useSocket();

  const query = useQuery({
    queryKey: ['team-chat', 'messages', targetUserId],
    queryFn: () => teamChatService.getMessages(targetUserId),
    enabled: !!targetUserId,
    staleTime: 0, // Always refetch from Redis when panel opens
  });

  // Append incoming messages from socket
  useEffect(() => {
    if (!socket || !roomId) return;

    const handleMessage = (event: TeamMessageEvent) => {
      if (event.roomId !== roomId) return;

      queryClient.setQueryData<{ messages: TeamChatMessage[]; roomId: string }>(
        ['team-chat', 'messages', targetUserId],
        (old) => {
          if (!old) return { messages: [event.message], roomId };
          // Avoid duplicate (in case sender also receives their own message)
          const exists = old.messages.some((m) => m.id === event.message.id);
          if (exists) return old;
          return { ...old, messages: [...old.messages, event.message] };
        }
      );
    };

    socket.on(SOCKET_EVENTS.TEAM_MESSAGE, handleMessage);
    return () => {
      socket.off(SOCKET_EVENTS.TEAM_MESSAGE, handleMessage);
    };
  }, [socket, roomId, targetUserId, queryClient]);

  return query;
};
